import * as Sentry from '@sentry/react';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { useAppContext } from '@contexts/AppContext';
import Markdown from 'markdown-to-jsx';

import { useAppSettingsQuery } from '@queries/AppQueries';
import { useCurrentEntertainerQuery } from '@queries/EntertainerQueries';
import { useCurrentUser, useEntertainerDashboardQuery } from '@queries/UserQueries';

import { useAnalytics } from '@hooks/useAnalytics';
import { useCapacitorUpdateAvailable } from '@hooks/useCapacitorUpdateAvailable';
import {
  CurrentCity,
  useCitiesQuery,
  useCityById,
  useCurrentUserCity,
} from '@hooks/useCurrentUserLocation';
import { usePWA } from '@hooks/usePWA';

import { useRouter } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button from '@components/buttons/Button';
import IconButton from '@components/buttons/IconButton';
import { AdvancedPieChart } from '@components/charts/AdvancedPieChart';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import Card from '@components/layout/Card/Card';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';
import { ReportOverlay } from '@components/overlays/ReportOverlay/ReportOverlay';

import { useInAppNotifications } from '@screens/NotificationsScreen/NotificationsScreen';
import { ChangeCityButton } from '@screens/SettingsScreen';
import { KingOfDragBanner } from '@screens/entertainer/DragKingCommunityScreen/KingOfDragBanner';

import { openSplashNotification } from '../../layouts/SplashNotificationPopup/useOpenSplashNotification';
import { ConversationsIcon, EntertainerCommunityScreen } from './CommunityScreen';
import './CommunityScreen.scss';
import { NonEntertainerCommunityScreen } from './NonEntertainerCommunityScreen';

const WeeklyScore: FC<{ city: CurrentCity | undefined }> = ({ city }) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();
  const { entertainerDashboardIsReady, entertainerDashboard } = useEntertainerDashboardQuery({
    cityId: city?._id,
  });

  if (!city || !entertainerDashboardIsReady) {
    return (
      <>
        <div>
          <div className="mb-3">
            <Skeleton height={116} />
          </div>
        </div>
      </>
    );
  }

  const { title, messages, chartData, actions } = entertainerDashboard.primaryCard;

  return (
    <>
      <div>
        <Card>
          <Card.Body>
            <div className="d-flex gap-2 align-items-center">
              <div style={{ width: 70, height: 60 }}>
                <AdvancedPieChart
                  showLabels
                  data={chartData}
                  labelFontSize={18}
                  labelPosition={0}
                  label={({ dataEntry }) =>
                    dataEntry.key === 'completed' ? `${dataEntry.value}%` : undefined
                  }
                />
              </div>
              <div>
                <div className="fs-5">
                  <strong>{title}</strong>
                </div>
                <Markdown className="opacity-75" options={{ forceWrapper: true }}>
                  {messages?.[0]}
                </Markdown>
              </div>
            </div>
            <div className="d-flex gap-2" style={{ marginTop: 12 }}>
              {actions.map((action) => (
                <div
                  key={action.label}
                  className="py-1 px-2 text-align-center w-100"
                  style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: 8 }}
                  onClick={() => {
                    track('Weekly Score Action Clicked', action);
                    navigate(action.to, { state: { back: '/', forceBack: true } });
                  }}
                >
                  <div style={{ padding: '2px 0 0' }}>
                    <img
                      src={action.iconUrl}
                      alt={action.label}
                      style={{ width: 24, height: 24, padding: '1px 0', objectFit: 'contain' }}
                    />
                  </div>
                  <div style={{ fontSize: '.6rem', lineHeight: 1 }}>{action.label}</div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      </div>
    </>
  );
};

const WeeklyMetrics: FC<{ city: CurrentCity | undefined }> = ({ city }) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();
  const { entertainerDashboardIsReady, entertainerDashboard } = useEntertainerDashboardQuery({
    cityId: city?._id,
  });

  if (!city || !entertainerDashboardIsReady) {
    return (
      <>
        <div>
          <div className="mb-2">
            <Skeleton height={22} />
          </div>
          <div className="mb-3">
            <Skeleton height={116} />
          </div>
        </div>
      </>
    );
  }

  const { title, weeklyMetrics } = entertainerDashboard.primaryTiles;

  return (
    <div className="w-100">
      <TitleToolbar text={title} size="sm" />
      <div className="d-flex gap-2">
        {weeklyMetrics.map((metric) => (
          <div
            key={metric.title}
            data-testid={`Weekly Metric: ${metric.title}`}
            className="w-100 h-100"
          >
            <Card indicator={metric.indicatorCount}>
              <div className="p-2">
                <div>
                  <div className="d-flex justify-content-between">
                    <div className="fs-4">
                      <strong className="count">{metric.count}</strong>
                    </div>
                    <div>
                      <IconButton
                        variant="smooth"
                        color="neutral"
                        onClick={() => {
                          track('Weekly Metric Arrow Clicked', metric);
                          navigate(metric.to, { state: { back: '/', forceBack: true } });
                        }}
                      >
                        <FontAwesomeIcon name="chevron-right" />
                      </IconButton>
                    </div>
                  </div>
                  <div style={{ fontSize: '.9rem', maxWidth: 110 }}>
                    <strong className="metric-title">{metric.title}</strong>
                  </div>
                </div>
                <div className="mt-2">
                  {metric.currentUserCount ? (
                    <Button
                      color="teal"
                      size="sm"
                      startIcon={<strong>{metric.currentUserCount}</strong>}
                      style={{ fontSize: '.7rem' }}
                    >
                      <span className="opacity-75">by you</span>
                    </Button>
                  ) : (
                    <Button
                      color="danger"
                      variant="outlined dashed"
                      startIcon={<FontAwesomeIcon name="plus" />}
                      size="sm"
                      style={{ fontSize: '.7rem' }}
                      onClick={() => {
                        track('Weekly Metric CTA Clicked', metric);
                        navigate(metric.ctaTo, { state: { back: '/', forceBack: true } });
                      }}
                    >
                      {metric.ctaText}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
};

const SuggestedActions: FC<{ city: CurrentCity | undefined; onOpenSupport: () => void }> = ({
  city,
  onOpenSupport,
}) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();
  const { entertainerDashboardIsReady, entertainerDashboard } = useEntertainerDashboardQuery({
    cityId: city?._id,
  });

  if (!city || !entertainerDashboardIsReady) {
    return (
      <>
        <div>
          <div className="mb-2">
            <Skeleton height={22} />
          </div>
          <div className="mb-3">
            <Skeleton height={116} />
          </div>
        </div>
      </>
    );
  }

  const { title, suggestedActions } = entertainerDashboard.secondaryTiles;

  return (
    <>
      <TitleToolbar text={title} size="sm" className="mb-1" />
      <HorizontalScroll
        items={[
          <div className="d-flex gap-2 pt-2 pe-4">
            {suggestedActions.map((action) => (
              <div key={action.title} className="h-100" style={{ width: 120 }}>
                <Card
                  indicator={action.indicatorCount}
                  className="h-100"
                  onClick={() => {
                    track('Suggested Action Clicked', action);

                    if (action.to === 'OpenSupportModal') {
                      onOpenSupport();
                      return;
                    }

                    navigate(action.to, { state: { back: '/', forceBack: true } });
                  }}
                >
                  <div className="p-2">
                    <div className="text-align-center">
                      <img
                        src={action.imgUrl}
                        alt={action.title}
                        style={{ width: 40, height: 40, objectFit: 'contain' }}
                      />
                    </div>
                    <div>
                      <div className="text-align-center" style={{ fontSize: '.9rem' }}>
                        <strong>{action.title}</strong>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            ))}
          </div>,
        ]}
      />
      <div className="mt-2">
        <KingOfDragBanner context="Entertainer Dashboard" />
      </div>
    </>
  );
};

const Featured: FC<{ city: CurrentCity | undefined }> = ({ city }) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();

  const { entertainerDashboardIsReady, entertainerDashboard } = useEntertainerDashboardQuery({
    cityId: city?._id,
  });

  if (!city || !entertainerDashboardIsReady) {
    return (
      <>
        <div>
          <div className="mb-2">
            <Skeleton height={22} />
          </div>
          <div className="mb-3">
            <Skeleton height={48} />
            <Skeleton height={48} />
            <Skeleton height={48} />
            <Skeleton height={48} />
          </div>
        </div>
      </>
    );
  }

  const { title, sections } = entertainerDashboard.featuredFooter;
  const featuredIconSize = 24;

  return (
    <>
      <TitleToolbar text={title} size="sm" className="mb-1" />
      <div className="mt-2">
        <div className="d-grid gap-2 mb-3">
          {sections.map((section) => (
            <div
              className="d-flex align-items-center gap-2 p-2"
              style={{ background: 'rgba(255,255,255,.1)', borderRadius: 8 }}
              onClick={() => {
                track('Featured Row Clicked', section);
                navigate(section.to, {
                  state: { back: '/', forceBack: true },
                });
              }}
            >
              <div
                style={{
                  height: featuredIconSize,
                  width: featuredIconSize,
                }}
              >
                <img
                  className="d-block"
                  src={section.imgUrl}
                  alt={section.title}
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: 2,
                  }}
                />
              </div>
              <div>
                <strong>{section.title}</strong>
              </div>
              <div className="ml-auto px-2 d-flex align-items-center gap-3">
                <div>{section.count ?? 0}</div>
                <FontAwesomeIcon name="chevron-right" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export const EntertainerDashboardScreen = createScreenComponent<{ cityId?: string }>(
  ({ f7route, cityId }) => {
    const { navigate } = useRouter();
    const { entertainer } = useCurrentEntertainerQuery();
    const { hasUnreadNotifications } = useInAppNotifications();
    const { currentCityIsReady, currentCity } = useCurrentUserCity({ forceFetch: true });
    const { cities } = useCitiesQuery();
    const { overrideCurrentCity } = useAppContext();
    const [supportOverlayIsVisible, setSupportOverlayIsVisible] = useState(false);

    useEffect(() => {
      if (cityId && cities?.length) {
        const cityOverride = cities.find((city) => city._id === cityId);
        if (cityOverride) {
          overrideCurrentCity(cityOverride);
        }
      }
    }, [cityId, cities]);

    const pwaPromptRef = useRef(false);
    const { canInstallPWA, promptPWAInstall } = usePWA();

    useEffect(() => {
      if (f7route.query.installPWA === 'true' && canInstallPWA && !pwaPromptRef.current) {
        pwaPromptRef.current = true;
        promptPWAInstall()
          .then(() => {
            pwaPromptRef.current = false;
          })
          .catch(() => {
            pwaPromptRef.current = false;
          });
      }
    }, []);

    const { refetchEntertainerDashboard } = useEntertainerDashboardQuery({
      cityId: currentCity?._id,
    });

    const handleReenter = () => {
      refetchEntertainerDashboard();
    };

    const title = useMemo(() => {
      if (!entertainer) {
        return <Skeleton height={28} />;
      }

      const rootMessage = `Hey ${entertainer?.title}!`;

      try {
        const isKing = entertainer?.details?.categories?.includes('Drag King');
        const isThing = entertainer?.details?.categories?.includes('Drag Thing');

        if (isThing) {
          return `${rootMessage} 👹`;
        }

        if (isKing) {
          return `${rootMessage} 👑`;
        }
      } catch (e) {
        Sentry.captureException(e);
      }

      return `${rootMessage} 💃🏽`;
    }, [entertainer]);

    return (
      <Screen
        name="Entertainer Dashboard Screen"
        disableLocationPermissionGate={false}
        onReenter={handleReenter}
        headerProps={{
          left: (
            <div className="ps-2 d-flex align-items-center" style={{ gap: 10 }}>
              <Screen.GagglTitle />
              <ChangeCityButton
                buttonProps={{
                  variant: 'smooth',
                  style: { padding: '4px 12px' },
                }}
              />
            </div>
          ),
          right: (
            <div className="d-flex gap-2">
              <ConversationsIcon />
              <IconButton
                color="neutral"
                variant="smooth"
                badge={hasUnreadNotifications}
                onClick={() => {
                  navigate('/notifications');
                }}
              >
                <FontAwesomeIcon name="bell" />
              </IconButton>
            </div>
          ),
        }}
      >
        <Screen.Content maxWidth={425}>
          <TitleToolbar
            text={<span style={{ fontSize: '1.5rem' }}>{title}</span>}
            className="title-toolbar-welcome mb-0"
            size="md"
          />
          {currentCity?.name ? (
            <p className="mt-0">{`Dive into the ${currentCity?.name} community`}</p>
          ) : (
            <div className="w-100 mb-2">
              <Skeleton height={26} />
            </div>
          )}
          <div className="mb-3">
            <WeeklyScore city={currentCity} />
          </div>
          <WeeklyMetrics city={currentCity} />
          <SuggestedActions
            city={currentCity}
            onOpenSupport={() => setSupportOverlayIsVisible(true)}
          />
          <Featured city={currentCity} />
        </Screen.Content>

        <ReportOverlay
          isVisible={supportOverlayIsVisible}
          title="Feedback & Support"
          content={
            <p>
              We're still in beta so we are VERY open to feedback. If you have a question or there's
              a bug that we missed. Please let us know so that we can fix it!
            </p>
          }
          submitButtonText="Send"
          data={{
            collectionName: 'support',
            objectId: entertainer?.userId,
          }}
          onClose={() => setSupportOverlayIsVisible(false)}
        />
      </Screen>
    );
  },
);

export const DashboardScreen = createScreenComponent<{ tab?: any }>((props) => {
  const { currentUser } = useCurrentUser();
  const { entertainer, entertainerIsReady } = useCurrentEntertainerQuery();
  const { appSettingsAreReady, appSettings } = useAppSettingsQuery();
  const { isCapacitorUpdateAvailable, updateApp } = useCapacitorUpdateAvailable();

  useEffect(() => {
    if (isCapacitorUpdateAvailable) {
      openSplashNotification({
        _id: 'ota-app-update',
        title: 'App Update Available',
        content: 'An update is available for the app. Would you like to update now?',
        displayPath: '*',
        button: {
          text: 'Update',
          to: '',
          action: updateApp,
        },
      });
    }
  }, [isCapacitorUpdateAvailable]);

  if (currentUser?.entertainerId || entertainer?._id) {
    return <EntertainerDashboardScreen {...props} />;
  }

  return <NonEntertainerCommunityScreen {...props} />;
});
